import { CaretDownOutlined, LoadingOutlined } from '@ant-design/icons';
import {
  Dropdown as AntdDropdown,
  MenuProps,
  Space,
  Tag as AntdTag,
  TagProps as AntdTagProps,
} from 'antd';
import { FC, ReactNode, useMemo, useState } from 'react';
import { colors } from 'theme/colors';

import { COLOR_BORDER_MAP } from 'components/tag/StatusColorConfig';
import { STATUS_CONFIG } from 'components/tag/StatusConfig';

import { OptionMenu } from 'types/tag/OptionMenu';
import { StatusType } from 'types/tag/StatusType';

interface TagProps extends Omit<AntdTagProps, 'onChange'> {
  status?: StatusType;
  label?: string | ReactNode;
  color?: AntdTagProps['color'];
  icon?: ReactNode;
  editable?: boolean;
  options?: OptionMenu;
  emptyTitle?: boolean;
  onChange?: (option: OptionMenu[number]) => void;
  defaultValue?: string;
  loading?: boolean;
}

/**
 * @param status - Render a pre-defined value,color,icon. CAN BE OVERRIDDEN.
 * @param label - The label of the badge (controlled).
 * @param color - The color of the badge.
 * @param icon - The icon of the badge.
 * @param loading - If true, show loading icon with "loading..." text.
 * @param editable - If true, the badge will be a dropdown menu.
 * @param options - If editable=true, this will be used to populate the dropdown menu.
 * @param onChange - The function to call when the editable badge is changed.
 * @param defaultValue - The default value of the badge (for editable badges).
 *
 */

const customTagColors = {
  'ledger-white': {
    backgroundColor: colors.ledgerWhite1,
    borderColor: colors.ledgerWhite5,
    color: colors.ledgerWhite8,
  },
  'subtle-mint': {
    backgroundColor: colors.subtleMint1,
    borderColor: colors.subtleMint5,
    color: colors.subtleMint8,
  },
};

export const Tag: FC<TagProps> = ({
  status = 'all',
  label,
  color,
  icon,
  loading = false,
  editable = false,
  options,
  emptyTitle = false,
  onChange = () => null,
  defaultValue,
  ...rest
}) => {
  const config = STATUS_CONFIG[status] ?? undefined;

  const needCustomStyle = useMemo(() => {
    if (color && customTagColors[color as keyof typeof customTagColors]) {
      return true;
    }
    return false;
  }, [color]);

  const resolvedBorderColor = color
    ? COLOR_BORDER_MAP[color]
    : config?.borderColor;

  if (loading) {
    return <AntdTag icon={<LoadingOutlined spin />}>Loading...</AntdTag>;
  }

  return !editable ? (
    <AntdTag
      color={color || config?.color || 'default'}
      icon={icon || config?.icon}
      style={
        needCustomStyle
          ? customTagColors[color as keyof typeof customTagColors]
          : resolvedBorderColor
            ? { ...rest.style, borderColor: resolvedBorderColor }
            : (rest.style ?? undefined)
      }
      {...rest}
    >
      {emptyTitle
        ? undefined
        : label || rest.children || config?.title || status}
    </AntdTag>
  ) : (
    <TagSelect
      options={options}
      onChange={onChange}
      defaultValue={defaultValue}
    />
  );
};

const TagSelect = ({
  options = [],
  onChange,
  defaultValue,
  ...rest
}: TagProps) => {
  const [current, setCurrent] = useState(
    options?.length > 0
      ? (options.find((option) => option.value === defaultValue) ?? options[0])
      : undefined
  );

  const items: MenuProps['items'] = options
    .filter((option) => option.value !== current?.value)
    .map((option) => ({
      key: option.value ?? '',
      label: (
        <AntdTag icon={option.icon} color={option.color}>
          {option.label}
        </AntdTag>
      ),
      onClick: () => {
        setCurrent(option);
        onChange?.(option);
      },
    }));

  return (
    <AntdDropdown
      menu={{
        items,
        selectable: true,
        defaultSelectedKeys: [current?.value ?? ''],
      }}
      trigger={['click']}
      className="cursor-pointer hover:brightness-95 transition-all mr-0"
    >
      <Space size={0}>
        <AntdTag
          color={current?.color}
          icon={current?.icon}
          className="mr-1 flex items-center gap-1 group"
          {...rest}
        >
          {current?.label}
          <CaretDownOutlined
            style={{ fontSize: 10 }}
            className="opacity-50 group-hover:opacity-100 ml-1"
          />
        </AntdTag>
      </Space>
    </AntdDropdown>
  );
};

Tag.displayName = 'Tag';
